import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Page,
  Card,
  BlockStack,
  InlineGrid,
  InlineStack,
  Box,
  Text,
  Badge,
  Banner,
  Button,
  Popover,
  ActionList,
  Select,
  IndexTable,
  Divider,
  TextField,
  Icon,
} from "@shopify/polaris";
import { CalendarIcon, SearchIcon, FilterIcon } from "@shopify/polaris-icons";
import { fetchAnalyticsSummary } from "../../api/analyticsApi";
import { fetchTemplates } from "../../api/templatesApi";
import { getPlacementLabel } from "../../utils/themeEditorLinks";
import {
  ANALYTICS_BAR_FILTER_OPTIONS,
  analyticsFilterLabel,
  filterAnalyticsBarRows,
  searchAnalyticsBarRows,
} from "../../utils/analyticsIndex";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";
import { AnalyticsPageSkeleton } from "../../components/PageSkeleton";

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function presetRange(preset) {
  if (preset === "all") return { from: null, to: null };
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  if (preset === "7d") start.setDate(start.getDate() - 6);
  else if (preset === "30d") start.setDate(start.getDate() - 29);
  else if (preset === "90d") start.setDate(start.getDate() - 89);
  else return { from: null, to: null };
  return { from: ymd(start), to: ymd(end) };
}

const RANGE_LABELS = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  all: "All time",
};

function fmtNumber(n) {
  return Number(n || 0).toLocaleString();
}

function fmtMoneyCents(cents, currency) {
  const amount = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    return `${(currency || "USD") + " "}${amount.toFixed(2)}`;
  }
}

function MetricTile({ label, value, hint, tone }) {
  return (
    <Box
      background="bg-surface"
      borderColor="border"
      borderWidth="025"
      borderRadius="300"
      padding="400"
      minHeight="100%"
    >
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="heading2xl" tone={tone || undefined}>
          {value}
        </Text>
        {hint ? (
          <Text as="p" variant="bodySm" tone="subdued">
            {hint}
          </Text>
        ) : null}
      </BlockStack>
    </Box>
  );
}

function formatDayLabel(isoDay) {
  if (!isoDay) return "";
  const parts = String(isoDay).slice(0, 10).split("-");
  if (parts.length !== 3) return isoDay;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function buildLinePath(points, yKey) {
  if (!points.length) return "";
  return points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p[yKey].toFixed(2)}`)
    .join(" ");
}

function buildAreaPath(points, yKey, baseline) {
  if (!points.length) return "";
  const top = buildLinePath(points, yKey);
  const last = points[points.length - 1];
  const first = points[0];
  return `${top} L ${last.x.toFixed(2)} ${baseline.toFixed(2)} L ${first.x.toFixed(2)} ${baseline.toFixed(2)} Z`;
}

/** Responsive area + line chart for daily impressions and goal-reached sessions. */
function TrendChart({ series }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0 });

  const data = series || [];

  if (data.length === 0) {
    return (
      <Box padding="600">
        <Text as="p" tone="subdued" alignment="center">
          No activity in this range yet.
        </Text>
      </Box>
    );
  }

  const W = 600;
  const H = 200;
  const pad = { l: 44, r: 16, t: 16, b: 32 };
  const chartW = W - pad.l - pad.r;
  const chartH = H - pad.t - pad.b;
  const baseline = pad.t + chartH;

  const maxY = Math.max(
    1,
    ...data.map((d) => Math.max(d.impressions || 0, d.goal_reached_sessions || 0)),
  );

  const yTicks = [0, Math.round(maxY * 0.5), maxY];

  const points = data.map((d, i) => {
    const x = pad.l + (data.length === 1 ? chartW / 2 : (i / (data.length - 1)) * chartW);
    const yImp = pad.t + chartH - ((d.impressions || 0) / maxY) * chartH;
    const yGoal = pad.t + chartH - ((d.goal_reached_sessions || 0) / maxY) * chartH;
    return { ...d, x, yImp, yGoal };
  });

  const areaPath = buildAreaPath(points, "yImp", baseline);
  const goalLinePath = buildLinePath(points, "yGoal");

  const labelStep = Math.max(1, Math.ceil(data.length / 6));
  const xLabels = points.filter((_, i) => i === 0 || i === points.length - 1 || i % labelStep === 0);

  const peak = data.reduce(
    (best, d) => ((d.impressions || 0) > (best.impressions || 0) ? d : best),
    data[0],
  );

  const hovered = hoverIdx != null ? points[hoverIdx] : null;

  const onChartMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((relX - pad.l) / chartW) * (data.length - 1));
    const clamped = Math.max(0, Math.min(data.length - 1, idx));
    setHoverIdx(clamped);
    const px = ((points[clamped].x / W) * rect.width);
    setTooltipPos({ left: px, top: 8 });
  };

  return (
    <Box padding="300">
      <BlockStack gap="300">
        <InlineStack gap="400" wrap>
          <Text as="span" variant="bodySm" tone="subdued">
            Peak day: {formatDayLabel(peak.day)} · {fmtNumber(peak.impressions)} impressions
          </Text>
        </InlineStack>

        <div className="fsb-trend-chart">
          <div className="fsb-trend-chart__wrap">
            {hovered ? (
              <div className="fsb-trend-chart__tooltip" style={{ left: tooltipPos.left, top: tooltipPos.top }}>
                <Text as="p" variant="bodySm" fontWeight="semibold">
                  {formatDayLabel(hovered.day)}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {fmtNumber(hovered.impressions)} impressions
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {fmtNumber(hovered.goal_reached_sessions)} goal reached
                </Text>
              </div>
            ) : null}
            <svg
              className="fsb-trend-chart__svg"
              viewBox={`0 0 ${W} ${H}`}
              role="img"
              aria-label="Daily impressions and goal reached trend"
              onMouseMove={onChartMove}
              onMouseLeave={() => setHoverIdx(null)}
            >
              <defs>
                <linearGradient id="fsb-trend-area-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5c8ef5" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#2c6ecb" stopOpacity="0.04" />
                </linearGradient>
              </defs>

              {yTicks.map((tick) => {
                const y = pad.t + chartH - (tick / maxY) * chartH;
                return (
                  <g key={tick}>
                    <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="#ebebeb" strokeWidth="1" />
                    <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#8c9196">
                      {tick >= 1000 ? `${Math.round(tick / 100) / 10}k` : tick}
                    </text>
                  </g>
                );
              })}

              <path d={areaPath} fill="url(#fsb-trend-area-fill)" />
              <path
                d={buildLinePath(points, "yImp")}
                fill="none"
                stroke="#2c6ecb"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                opacity="0.85"
              />

              <path
                d={goalLinePath}
                fill="none"
                stroke="#008060"
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
              />

              {points.map((p, i) => (
                <circle
                  key={p.day}
                  cx={p.x}
                  cy={p.yGoal}
                  r={hoverIdx === i ? 5 : 3.5}
                  fill="#008060"
                  stroke="#fff"
                  strokeWidth="2"
                />
              ))}

              {hoverIdx != null && points[hoverIdx] ? (
                <line
                  x1={points[hoverIdx].x}
                  y1={pad.t}
                  x2={points[hoverIdx].x}
                  y2={baseline}
                  stroke="#2c6ecb"
                  strokeWidth="1"
                  strokeDasharray="4 3"
                  opacity="0.5"
                />
              ) : null}

              {xLabels.map((p) => (
                <text key={`lbl-${p.day}`} x={p.x} y={H - 8} textAnchor="middle" fontSize="10" fill="#6d7175">
                  {formatDayLabel(p.day)}
                </text>
              ))}
            </svg>
          </div>
        </div>

        <InlineStack gap="500" wrap>
          <InlineStack gap="150" blockAlign="center">
            <span className="fsb-trend-chart__legend-dot fsb-trend-chart__legend-dot--area" />
            <Text as="span" variant="bodySm" tone="subdued">
              Impressions
            </Text>
          </InlineStack>
          <InlineStack gap="150" blockAlign="center">
            <span className="fsb-trend-chart__legend-dot fsb-trend-chart__legend-dot--line" />
            <Text as="span" variant="bodySm" tone="subdued">
              Goal reached (sessions)
            </Text>
          </InlineStack>
        </InlineStack>
      </BlockStack>
    </Box>
  );
}

function MessageStateBars({ states }) {
  const total = (states.initial || 0) + (states.progress || 0) + (states.success || 0);
  const rows = [
    { label: "Initial (empty cart)", value: states.initial || 0, color: "#9ca3af" },
    { label: "In progress", value: states.progress || 0, color: "#f59e0b" },
    { label: "Goal reached", value: states.success || 0, color: "#16a34a" },
  ];
  if (total === 0) {
    return (
      <Text as="p" tone="subdued">
        No impressions yet.
      </Text>
    );
  }
  return (
    <BlockStack gap="300">
      {rows.map((r) => {
        const pct = Math.round((r.value / total) * 100);
        return (
          <BlockStack gap="100" key={r.label}>
            <InlineStack align="space-between">
              <Text as="span" variant="bodySm">{r.label}</Text>
              <Text as="span" variant="bodySm" tone="subdued">
                {fmtNumber(r.value)} ({pct}%)
              </Text>
            </InlineStack>
            <div style={{ height: 8, background: "#f1f2f3", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: r.color }} />
            </div>
          </BlockStack>
        );
      })}
    </BlockStack>
  );
}

export default function AnalyticsPage() {
  const [rangeMode, setRangeMode] = useState("30d");
  const [range, setRange] = useState(() => presetRange("30d"));
  const [rangeOpen, setRangeOpen] = useState(false);
  const [barFilter, setBarFilter] = useState("");
  const [barOptions, setBarOptions] = useState([{ label: "All bars", value: "" }]);
  const [tableSearch, setTableSearch] = useState("");
  const [tableFilter, setTableFilter] = useState("all");
  const [tableFilterOpen, setTableFilterOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useAppTitleBar({ title: "Analytics" });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchTemplates();
        const list = res?.data?.templates ?? [];
        const opts = [
          { label: "All bars", value: "" },
          ...list
            .filter((b) => b.public_id)
            .map((b) => ({ label: b.name || "Untitled bar", value: b.public_id })),
        ];
        setBarOptions(opts);
      } catch (e) {
        /* non-fatal */
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAnalyticsSummary({
        from: range.from,
        to: range.to,
        publicId: barFilter || undefined,
      });
      setData(res?.data || null);
    } catch (e) {
      setError(e?.message || "Could not load analytics");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, barFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyPreset = useCallback((preset) => {
    setRangeMode(preset);
    setRange(presetRange(preset));
    setRangeOpen(false);
  }, []);

  const rangeLabel = RANGE_LABELS[rangeMode] || "Last 30 days";

  const overview = data?.overview || {};
  const orders = data?.orders || {};
  const currency = orders.currency || "USD";

  const perBarRows = useMemo(() => data?.per_bar || [], [data]);

  const visiblePerBarRows = useMemo(() => {
    let next = filterAnalyticsBarRows(perBarRows, tableFilter);
    next = searchAnalyticsBarRows(next, tableSearch);
    return next.sort((a, b) => (b.impressions || 0) - (a.impressions || 0));
  }, [perBarRows, tableFilter, tableSearch]);

  const tableFilterItems = useMemo(
    () =>
      ANALYTICS_BAR_FILTER_OPTIONS.map((opt) => ({
        content: opt.label,
        active: tableFilter === opt.value,
        onAction: () => {
          setTableFilter(opt.value);
          setTableFilterOpen(false);
        },
      })),
    [tableFilter],
  );

  return (
    <Page>
      <BlockStack gap="500">
        {error ? (
          <Banner tone="critical" onDismiss={() => setError(null)} title="Could not load analytics">
            {error}
          </Banner>
        ) : null}

        <Card>
          <InlineStack gap="300" wrap blockAlign="center" align="space-between">
            <Text as="h2" variant="headingMd">
              Performance overview
            </Text>
            <InlineStack gap="300" blockAlign="center" wrap>
              <div style={{ minWidth: 200 }}>
                <Select
                  label="Bar"
                  labelHidden
                  options={barOptions}
                  value={barFilter}
                  onChange={setBarFilter}
                />
              </div>
              <Popover
                active={rangeOpen}
                onClose={() => setRangeOpen(false)}
                activator={
                  <Button icon={CalendarIcon} disclosure onClick={() => setRangeOpen((v) => !v)}>
                    {rangeLabel}
                  </Button>
                }
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    { content: "Last 7 days", onAction: () => applyPreset("7d") },
                    { content: "Last 30 days", onAction: () => applyPreset("30d") },
                    { content: "Last 90 days", onAction: () => applyPreset("90d") },
                    { content: "All time", onAction: () => applyPreset("all") },
                  ]}
                />
              </Popover>
            </InlineStack>
          </InlineStack>
        </Card>

        {loading ? (
          <AnalyticsPageSkeleton />
        ) : (
          <>
            <Text as="h3" variant="headingSm">
              Engagement
            </Text>
            <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
              <MetricTile label="Impressions" value={fmtNumber(overview.impressions)} hint={`${fmtNumber(overview.unique_visitors)} unique visitors`} />
              <MetricTile
                label="Goal reached"
                value={`${overview.goal_reached_rate || 0}%`}
                hint={`${fmtNumber(overview.goal_reached_sessions)} of ${fmtNumber(overview.sessions)} sessions`}
                tone="success"
              />
              <MetricTile label="Avg progress to goal" value={`${overview.avg_progress_pct || 0}%`} hint={`Avg cart ${fmtMoneyCents(overview.avg_cart_cents, currency)}`} />
              <MetricTile label="Upsell clicks" value={fmtNumber(overview.upsell_clicks)} hint={`${overview.upsell_ctr || 0}% of impressions`} />
            </InlineGrid>

            <Text as="h3" variant="headingSm">
              Revenue &amp; conversion
            </Text>
            <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
              <MetricTile label="Orders" value={fmtNumber(orders.orders)} hint={`${fmtNumber(orders.attributed_orders)} saw the bar`} />
              <MetricTile label="Revenue" value={fmtMoneyCents(orders.revenue_cents, currency)} hint={`AOV ${fmtMoneyCents(orders.aov_cents, currency)}`} />
              <MetricTile
                label="Reached free-shipping goal"
                value={fmtNumber(orders.qualifying_orders)}
                hint={`${fmtMoneyCents(orders.qualifying_revenue_cents, currency)} revenue`}
                tone="success"
              />
              <MetricTile label="Conversion (saw bar → order)" value={`${orders.conversion_rate || 0}%`} hint={`${fmtMoneyCents(orders.attributed_revenue_cents, currency)} attributed`} />
            </InlineGrid>

            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
              <Card>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">
                    Daily trend
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Hover the chart to see daily impressions and goal-reached sessions.
                  </Text>
                  <TrendChart series={data?.timeseries || []} />
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">
                    Message states
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    What shoppers saw when the bar appeared.
                  </Text>
                  <Divider />
                  <MessageStateBars states={data?.message_states || {}} />
                </BlockStack>
              </Card>
            </InlineGrid>

            <Card padding="0">
              <Box padding="400">
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">
                    Per-bar breakdown
                  </Text>
                  {perBarRows.length > 0 ? (
                    <InlineStack gap="300" wrap blockAlign="center">
                      <div style={{ flex: "1 1 280px", minWidth: 220 }}>
                        <TextField
                          label="Search bars"
                          labelHidden
                          placeholder="Search name or placement…"
                          value={tableSearch}
                          onChange={setTableSearch}
                          prefix={<Icon source={SearchIcon} />}
                          autoComplete="off"
                          clearButton
                          onClearButtonClick={() => setTableSearch("")}
                        />
                      </div>
                      <Popover
                        active={tableFilterOpen}
                        onClose={() => setTableFilterOpen(false)}
                        activator={
                          <Button icon={FilterIcon} onClick={() => setTableFilterOpen((v) => !v)} disclosure>
                            {analyticsFilterLabel(tableFilter)}
                          </Button>
                        }
                      >
                        <ActionList actionRole="menuitemradio" items={tableFilterItems} />
                      </Popover>
                    </InlineStack>
                  ) : null}
                  {perBarRows.length > 0 ? (
                    <Text as="p" variant="bodySm" tone="subdued">
                      Showing {visiblePerBarRows.length} of {perBarRows.length} active bars
                    </Text>
                  ) : null}
                </BlockStack>
              </Box>
              {perBarRows.length === 0 ? (
                <Box padding="600">
                  <Text as="p" tone="subdued" alignment="center">
                    No bar activity in this range yet.
                  </Text>
                </Box>
              ) : visiblePerBarRows.length === 0 ? (
                <Box padding="600">
                  <Text as="p" tone="subdued" alignment="center">
                    No bars match your search or filter.
                  </Text>
                </Box>
              ) : (
                <IndexTable
                  resourceName={{ singular: "bar", plural: "bars" }}
                  itemCount={visiblePerBarRows.length}
                  selectable={false}
                  headings={[
                    { title: "Bar" },
                    { title: "Placement" },
                    { title: "Impressions" },
                    { title: "Goal reached" },
                    { title: "Upsell clicks" },
                  ]}
                >
                  {visiblePerBarRows.map((row, index) => (
                    <IndexTable.Row id={row.public_id || String(index)} key={row.public_id || index} position={index}>
                      <IndexTable.Cell>
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {row.name}
                        </Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>{getPlacementLabel(row.placement_type)}</IndexTable.Cell>
                      <IndexTable.Cell>{fmtNumber(row.impressions)}</IndexTable.Cell>
                      <IndexTable.Cell>
                        <Badge tone={row.goal_reached_rate >= 50 ? "success" : "attention"}>
                          {`${row.goal_reached_rate || 0}%`}
                        </Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>{fmtNumber(row.upsell_clicks)}</IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              )}
            </Card>
          </>
        )}
      </BlockStack>
    </Page>
  );
}
